/**
 * สถานะการนำทางภายในหน้าจอตามสอบ Lot และป้ายกำกับที่ใช้ร่วมกัน
 *
 * ทำไมใช้ query string ไม่ใช่ path ย่อย: `App.tsx` ประกาศ route ของทุกโมดูลจาก
 * `DESKTOP_MODULE_ROUTES` เส้นละหนึ่ง path แบบตรงตัว และ Work Order กำหนดว่า
 * หน้าจอนี้ใช้ path ที่ลงทะเบียนไว้แล้วเส้นเดียว (`/traceability/lots`) ห้ามคิด path
 * ที่สองขึ้นมาเอง — เหตุผลเดียวกับ `goodsReceipts/goodsReceiptView.ts`
 *
 * ผลพลอยได้ที่สำคัญกับหน้าจอนี้เป็นพิเศษ: **lot ที่กำลังดูอยู่อยู่ใน URL** การไล่
 * ตาม edge ทีละ hop จึงเป็นประวัติของเบราว์เซอร์จริง ปุ่ม back พากลับไป lot ก่อน
 * หน้า และลิงก์ของ lot หนึ่งส่งต่อให้คนอื่นเปิดดูจุดเดียวกันได้
 */

import {
  qualityStatusLabel,
  qualityStatusTone,
} from '../goodsReceipts/goodsReceiptView';
import {
  GENEALOGY_BACKWARD,
  GENEALOGY_FORWARD,
  type GenealogyDirection,
} from '../../lib/lotTraceability/types';

/**
 * ป้ายสถานะคุณภาพ — ใช้ฟังก์ชันเดียวกับหน้าจอรับเข้าสต๊อก ไม่ได้เขียนใหม่
 *
 * ทั้งสองตอบคำถามเดียวกันเป๊ะ ("`quality_status` ที่เซิร์ฟเวอร์ส่งมาแปลว่าของกองนี้
 * เบิกไปใช้ได้หรือยัง" ตาม D-059) และไม่มีอะไรเกี่ยวกับการรับเข้าอยู่ในตัวมันเลย
 * การคัดลอกมาไว้ที่นี่จะได้กฎเดียวกันสองชุดที่วันหนึ่งจะเลื่อนออกจากกัน — แล้ว lot
 * เดียวกันจะอ่านว่า "ปล่อยผ่านแล้ว" บนหน้าหนึ่งและ "ยังถูกกัก" บนอีกหน้าหนึ่ง
 *
 * ⚠️ ที่ยังไม่ยกขึ้นไปเป็น helper กลาง เพราะ `desktop/goodsReceipts/**` และที่ที่
 * ควรจะเป็นบ้านของมันอยู่นอก write scope ของ Work Order นี้ — เป็นข้อเสนอให้
 * `erp-orchestrator` ไม่ใช่การตัดสินใจของหน้าจอนี้ (เหตุผลเดียวกับที่
 * `goodsReceiptView.ts` ยืม `selectablePlants` มาจากหน้าจอใบสั่งผลิต)
 */
export { qualityStatusLabel, qualityStatusTone };

export const LOT_ID_PARAM = 'lot_id';

export type LotTraceabilityView =
  /** ยังไม่ได้ระบุ lot — หน้าจอถามหา lot ก่อน ไม่ยิงคำขอที่ไม่มีประธาน */
  | { readonly kind: 'prompt' }
  | { readonly kind: 'lot'; readonly lotId: string };

/** แปลง query เป็นหน้าจอที่ต้องแสดง — `lot_id` ว่างเท่ากับยังไม่ได้ระบุ */
export function resolveLotTraceabilityView(params: URLSearchParams): LotTraceabilityView {
  const lotId = params.get(LOT_ID_PARAM)?.trim() ?? '';
  return lotId === '' ? { kind: 'prompt' } : { kind: 'lot', lotId };
}

/** ลิงก์ไปยัง lot หนึ่ง — path เดิม เปลี่ยนแค่ query (การไล่ตาม edge ใช้ตัวนี้) */
export function lotTraceabilitySearch(lotId: string): string {
  return `?${LOT_ID_PARAM}=${encodeURIComponent(lotId)}`;
}

/* ------------------------------------------------------------- ป้ายกำกับ -- */

export interface DirectionCopy {
  /** ชื่อของทิศทางในภาษาที่ผู้ใช้ถาม ไม่ใช่ชื่อของ endpoint */
  readonly title: string;
  /** ทำไมถึงเปิดดูทิศทางนี้ — ต่างกันคนละงานกันโดยสิ้นเชิง */
  readonly purpose: string;
  /** หัวคอลัมน์ของ lot ที่เกี่ยวข้อง — "ใช้อะไรมา" กับ "ไปอยู่ที่ไหน" ไม่ใช่คำเดียวกัน */
  readonly relatedLotHeading: string;
  /** ความหมายของ `qty_consumed` ในทิศทางนี้ */
  readonly quantityHeading: string;
  /** ข้อความตอน `edges` ว่าง — ต้องอ่านออกว่าเป็นคำตอบ ไม่ใช่ความล้มเหลว */
  readonly emptyTitle: string;
  readonly emptyText: string;
}

/**
 * ข้อความประจำแต่ละทิศทาง — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript
 * เป็นผู้พิสูจน์ว่าไม่มีทิศทางไหนตกหล่น
 *
 * สองทิศทางนี้ **ไม่ใช่มุมกลับของกันและกันในสายตาผู้ใช้**: `backward` ตอบว่า
 * "ของเสียชิ้นนี้มาจากวัตถุดิบกองไหน" (หาสาเหตุ) ส่วน `forward` ตอบว่า "วัตถุดิบ
 * กองที่ปนเปื้อนไปอยู่ในสินค้าตัวไหนบ้าง" (เรียกคืน) การใช้ข้อความกลาง ๆ ชุดเดียว
 * จะทิ้งให้ผู้ใช้เป็นผู้แปลเองว่ากำลังอ่านอะไรอยู่
 */
export function directionCopy(direction: GenealogyDirection): DirectionCopy {
  switch (direction) {
    case GENEALOGY_BACKWARD:
      return {
        title: 'สืบย้อนกลับ — lot นี้ทำมาจากอะไร',
        purpose:
          'ใช้ตอนหาสาเหตุ: เมื่อ lot นี้มีปัญหา รายการด้านล่างคือวัตถุดิบ/กึ่งสำเร็จรูปที่ถูกเบิกมาใช้ผลิตมันโดยตรง',
        relatedLotHeading: 'lot ที่ถูกใช้ไป (parent)',
        quantityHeading: 'จำนวนที่ถูกใช้',
        emptyTitle: 'lot นี้ไม่ได้ถูกผลิตมาจาก lot อื่น',
        emptyText:
          'เซิร์ฟเวอร์ตอบสำเร็จแต่ไม่มีความสัมพันธ์ขาเข้าเลย — เป็นคำตอบปกติของ lot ที่เข้าระบบมาทางการรับเข้าสต๊อกแทนที่จะเกิดจากการผลิต ไม่ใช่การโหลดที่ล้มเหลว',
      };
    case GENEALOGY_FORWARD:
      return {
        title: 'สืบไปข้างหน้า — lot นี้ไปอยู่ที่ไหนต่อ',
        purpose:
          'ใช้ตอนเรียกคืนสินค้า: เมื่อ lot นี้ต้องถูกตามกลับ รายการด้านล่างคือ lot ที่ถูกผลิตขึ้นโดยใช้มันโดยตรง',
        relatedLotHeading: 'lot ที่ถูกผลิตขึ้น (child)',
        quantityHeading: 'จำนวนของ lot นี้ที่ถูกใช้ไป',
        emptyTitle: 'lot นี้ยังไม่ถูกเบิกไปใช้ผลิตอะไร',
        emptyText:
          'เซิร์ฟเวอร์ตอบสำเร็จแต่ไม่มีความสัมพันธ์ขาออกเลย — เป็นคำตอบปกติของของที่ยังอยู่ในคลังและยังไม่ถูกเบิก ไม่ใช่การโหลดที่ล้มเหลว',
      };
  }
}

/**
 * คำอธิบายของการมีหลาย edge — merge กับ split เป็นคำตอบปกติ ไม่ใช่ความผิดปกติ
 *
 * เขียนไว้ที่หน้าจอเพราะผู้ใช้ที่เห็นสาม parent บนสินค้าหนึ่งตัวมักตีความว่าข้อมูล
 * ซ้ำ แล้วเลือกมาบรรทัดเดียวไปทำงานต่อ ซึ่งบนงานเรียกคืนสินค้าแปลว่าตกไปสองกอง
 */
export function multipleEdgesNote(direction: GenealogyDirection, count: number): string | null {
  if (count < 2) {
    return null;
  }
  return direction === GENEALOGY_BACKWARD
    ? `${count} รายการแปลว่า lot นี้ถูกรวม (merge) มาจากหลาย lot ต้นทาง — ทุกบรรทัดเป็นของจริงคนละกอง ไม่ใช่ข้อมูลซ้ำ`
    : `${count} รายการแปลว่า lot นี้ถูกแบ่ง (split) ไปยังหลาย lot ปลายทาง — ต้องตามครบทุกบรรทัด ไม่ใช่เฉพาะบรรทัดแรก`;
}
